import { useMemo, useState } from 'react';

/**
 * Lightweight multi-series line chart (pure SVG, no deps).
 * Props:
 *  - series: [{ name, color, data: [{ x: 'YYYY-MM-DD', y: number }] }]
 *  - height?: number (default 180)
 *  - yFormat?: (v) => string
 *  - xTickEvery?: number (default auto)
 */
export function LineChart({ series = [], height = 180, yFormat = (v) => String(v), xTickEvery, emptyMsg = 'No data yet' }) {
  const width = 720;      // scales via viewBox
  const padding = { top: 12, right: 12, bottom: 26, left: 38 };

  const { xs, ys, xCount } = useMemo(() => {
    const allX = new Set();
    const allY = [];
    for (const s of series) for (const p of s.data) { allX.add(p.x); allY.push(p.y); }
    const xs = Array.from(allX).sort();
    return { xs, ys: allY, xCount: xs.length };
  }, [series]);

  const [tip, setTip] = useState(null);

  if (xCount === 0 || series.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-xs text-[#9EABCE]/60">{emptyMsg}</div>
    );
  }

  const maxY = Math.max(1, ...ys);
  const minY = 0;
  const plotW = width - padding.left - padding.right;
  const plotH = height - padding.top - padding.bottom;
  const xStep = xCount > 1 ? plotW / (xCount - 1) : plotW;

  const xIndex = Object.fromEntries(xs.map((x, i) => [x, i]));
  const xFor = (x) => padding.left + xIndex[x] * xStep;
  const yFor = (y) => padding.top + plotH - ((y - minY) / (maxY - minY || 1)) * plotH;

  const tickEvery = xTickEvery || Math.max(1, Math.ceil(xCount / 8));

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" preserveAspectRatio="none">
        {/* Y grid */}
        {[0, 0.5, 1].map((t) => {
          const rawLabel = minY + t * (maxY - minY);
          const label = (maxY - minY) >= 5 ? Math.round(rawLabel) : Number(rawLabel.toFixed(1));
          const y = padding.top + plotH - t * plotH;
          return (
            <g key={t}>
              <line x1={padding.left} x2={width - padding.right} y1={y} y2={y} stroke="rgba(255,255,255,0.06)" />
              <text x={8} y={y + 3} fontSize="9" fill="#9EABCE" opacity="0.7">{yFormat(label)}</text>
            </g>
          );
        })}
        {/* Lines */}
        {series.map((s) => {
          const path = s.data
            .filter(p => xIndex[p.x] !== undefined && p.y !== null && p.y !== undefined)
            .sort((a, b) => xIndex[a.x] - xIndex[b.x])
            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(p.x).toFixed(2)} ${yFor(p.y).toFixed(2)}`)
            .join(' ');
          return (
            <g key={s.name}>
              <path d={path} fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              {s.data.map(p => (
                xIndex[p.x] !== undefined && p.y !== null && p.y !== undefined ? (
                  <circle
                    key={`${s.name}-${p.x}`}
                    cx={xFor(p.x)} cy={yFor(p.y)} r={2.5}
                    fill={s.color}
                    onMouseEnter={() => setTip({ x: p.x, values: series.map(ss => { const pp = ss.data.find(q => q.x === p.x); return { name: ss.name, color: ss.color, y: pp?.y ?? null }; }) })}
                    onMouseLeave={() => setTip(null)}
                  />
                ) : null
              ))}
            </g>
          );
        })}
        {/* X ticks */}
        {xs.map((x, i) => (i % tickEvery === 0) && (
          <text key={x} x={xFor(x)} y={height - 6} fontSize="9" fill="#9EABCE" opacity="0.7" textAnchor="middle">
            {x.slice(5)}
          </text>
        ))}
      </svg>
      {/* Legend */}
      <div className="flex items-center flex-wrap gap-3 mt-2 px-1">
        {series.map(s => (
          <div key={s.name} className="flex items-center gap-1.5 text-[10px] text-[#9EABCE]">
            <span className="w-3 h-0.5 rounded" style={{ background: s.color }} />
            {s.name}
          </div>
        ))}
      </div>
      {tip && (
        <div className="pointer-events-none absolute top-1 right-1 rounded-lg bg-[#060A1A]/95 border border-white/10 px-2.5 py-1.5 text-[10px] text-white shadow-xl">
          <div className="text-[#9EABCE] mb-0.5">{tip.x}</div>
          {tip.values.map(v => (
            <div key={v.name} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: v.color }} />
              <span>{v.name}</span>
              <span className="ml-auto font-bold">{v.y === null ? '—' : yFormat(v.y)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
